import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { Logo } from "../brand/Logo";
import { brand, contact } from "../../content/site";
import { services } from "../../content/services";

const Col = ({ title, children }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-400">{title}</p>
    <ul className="mt-5 space-y-3 text-sm text-stone-300">{children}</ul>
  </div>
);

const FLink = ({ to, children, id }) => (
  <li><Link to={to} data-testid={id} className="tc-link transition-colors duration-200 hover:text-orange-400">{children}</Link></li>
);

export const Footer = () => (
  <footer className="relative overflow-hidden border-t border-white/10 bg-[#0A0807] text-stone-300" data-testid="site-footer">
    <div aria-hidden="true" className="tc-grid-bg absolute inset-0 opacity-30" />
    <div aria-hidden="true" className="absolute left-1/2 top-0 h-[420px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-600/[0.12] blur-[120px]" />
    <div className="relative mx-auto max-w-[1440px] px-5 pb-10 pt-24 sm:px-8 sm:pt-32 lg:px-12">
      <div className="grid gap-10 border-b border-white/10 pb-16 lg:grid-cols-12 lg:items-end" data-testid="footer-promise">
        <p className="font-display text-3xl font-medium leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:col-span-9 lg:text-6xl">
          You take care of business.<br /><span className="tc-copper-text">We take care of the tech.</span>
        </p>
        <div className="lg:col-span-3 lg:text-right">
          <Link to="/contact" data-testid="footer-cta" className="tc-btn-primary inline-flex h-14 items-center gap-3 rounded-full px-8 text-sm font-semibold">
            Start a conversation <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <div className="grid gap-14 pt-16 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo className="h-32 w-auto sm:h-40" />
          <p className="mt-8 font-display text-lg text-stone-100">{brand.mantra}</p>
          <p className="mt-1 text-sm text-stone-400">{brand.philosophy}</p>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">Kenya · Africa · Beyond</p>
          <address className="mt-6 text-sm not-italic leading-relaxed text-stone-300" data-testid="footer-address">
            <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{contact.address.label}</span>
            {contact.address.lines.join(", ")}
          </address>
        </div>
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-8">
          <Col title="Services">
            {services.slice(0, 8).map((s) => <FLink key={s.slug} to={`/services/${s.slug}`}>{s.name}</FLink>)}
          </Col>
          <Col title="Company">
            <FLink to="/#about">About</FLink>
            <FLink to="/#our-name">Our Name</FLink>
            <FLink to="/#industries">Industries</FLink>
            <FLink to="/#vision">Vision</FLink>
            <FLink to="/brendan">Brendan (in development)</FLink>
            <FLink to="/shop">Care-Shop (coming soon)</FLink>
          </Col>
          <Col title="Get in touch">
            <FLink to="/contact" id="footer-quote-link">Request a quote</FLink>
            <FLink to="/contact?type=site_survey">Book a site survey</FLink>
            <FLink to="/contact?type=contact">General enquiry</FLink>
            <FLink to="/track" id="footer-track-link">Track my request</FLink>
            {contact.email && <li><a href={`mailto:${contact.email}`} data-testid="footer-email-link" className="text-stone-300 transition-colors duration-200 hover:text-orange-400">{contact.email}</a></li>}
            {contact.phones.map((p) => (
              <li key={p.id} className="text-stone-300">
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{p.region}</span>
                <a href={`tel:${p.tel}`} data-testid={`footer-phone-${p.id}`} aria-label={`Call TechCare, ${p.region}: ${p.display}`}
                  className="whitespace-nowrap transition-colors duration-200 hover:text-orange-400">{p.display}</a>
                <a href={p.whatsapp} target="_blank" rel="noopener noreferrer" data-testid={`footer-whatsapp-${p.id}`} aria-label={`WhatsApp TechCare, ${p.region}: ${p.display} (opens WhatsApp)`}
                  className="flex min-h-8 w-fit items-center gap-1.5 py-1 text-xs text-orange-300 transition-colors duration-200 hover:text-orange-200">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              </li>
            ))}
            <li className="pt-2 text-xs leading-relaxed text-stone-400">Use a form for a tracked request with a reference number.</li>
          </Col>
        </div>
      </div>
      <div className="mt-20 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-stone-400 sm:flex-row sm:items-center sm:justify-between">
        <p>© {new Date().getFullYear()} {brand.name}. {brand.mantra}</p>
        <p className="font-mono tracking-wider">techcare.live</p>
      </div>
    </div>
  </footer>
);
